import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Field, Forms } from '@ofself/shell';
import { useToast } from '../components/common';
import api from '../services/api';
import {
  deriveEscrowKey,
  unwrapKeyFromEscrow,
  deriveWrappingKey,
  wrapPrivateKey,
} from '../utils/fieldEncryption';
import '../styles/Auth.css';

function ForgotPassword() {
  const [identifier, setIdentifier] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const result = await api.recoveryResetPassword(identifier.trim(), recoveryCode.trim(), newPassword, confirmPassword);

      // Attempt key re-wrap so encrypted data remains accessible after the reset
      const { enc_key_escrow, user_id, enc_public_key, access_token } = result || {};
      if (enc_key_escrow && user_id) {
        try {
          api.setToken(access_token);
          const escrowKey = await deriveEscrowKey(recoveryCode.trim(), user_id);
          const privateKey = await unwrapKeyFromEscrow(enc_key_escrow, escrowKey);
          const newWrappingKey = await deriveWrappingKey(newPassword, user_id);
          const newWrapped = await wrapPrivateKey(privateKey, newWrappingKey);
          await api.request('/auth/enc-keys', {
            method: 'PUT',
            body: JSON.stringify({ enc_public_key, enc_private_key_wrapped: newWrapped }),
          });
          api.setToken(null); // Clear temporary token; user must log in fresh
          toast.success('Password reset successful. Your encrypted data has been recovered. Please sign in.');
        } catch {
          api.setToken(null);
          toast.warning('Password reset successful, but encrypted data could not be re-keyed. Please sign in.');
        }
      } else {
        toast.success('Password reset successful. Please sign in.');
      }

      navigate('/login');
    } catch (err) {
      toast.error(err.message || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  // The kit's form-folio (<Forms>, shell 0.75.0) — the template these pages
  // originated. The app owns state and routing; the kit owns geometry + voice.
  return (
    <Field>
      <Forms
        statement="Reset your password"
        mark={<img src="/ofself-logo.svg" alt="" />}
        onSubmit={handleSubmit}
        dense
      >
        <Forms.Field label="Email or Username" htmlFor="identifier" required>
          <input
            id="identifier"
            type="text"
            placeholder="your@email.com or username"
            required
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
          />
        </Forms.Field>

        <Forms.Field label="Recovery Code" htmlFor="recoveryCode" required>
          <input
            id="recoveryCode"
            type="text"
            placeholder="ABCD-EF12"
            required
            value={recoveryCode}
            onChange={(e) => setRecoveryCode(e.target.value)}
          />
        </Forms.Field>

        <Forms.Field label="New Password" htmlFor="newPassword" required>
          <input
            id="newPassword"
            type="password"
            autoComplete="new-password"
            placeholder="••••••••"
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </Forms.Field>

        <Forms.Field label="Confirm New Password" htmlFor="confirmPassword" required>
          <input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            placeholder="••••••••"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </Forms.Field>

        <Forms.Cta busy={loading}>
          {loading ? 'Resetting...' : 'Reset Password'}
        </Forms.Cta>

        <Forms.Foot>
          <Forms.FootCell href="/login" onNavigate={navigate}>
            <span>Remembered your password? <strong>Back to sign in↗</strong></span>
          </Forms.FootCell>
        </Forms.Foot>
      </Forms>
    </Field>
  );
}

export default ForgotPassword;
